<template>
<div>
	<div class="my-1 row">

		<div class="col-md-8">
			<h1 class="display-4">{{ $t('common.pendingBids') }}</h1>
		</div>

		<div class="col-sm-4">
			<b-pagination-nav
				:link-gen="linkGen"
			    :number-of-pages="pendingBids.meta.totalPages"
			    :value="pendingBids.meta.currentPage"
			    use-router
			    class="pull-right"
			/>
		</div>
	</div>

	<div class="row my-1">
		<div class="col-sm-4">
			<b-form-input v-model="filter"
						  autocomplete="off"
			              :placeholder="$t('pendingBid.searchPlaceholder')" />
			<b-button
			  	variant="outline-primary"
			    size="sm"
			    class="my-3"
				@click="resetSorting(true)"
			>
				{{ $t('common.resetSort') }}
			</b-button>
		</div>
	</div>

	<partial-loader :loading="pendingBids.isFetching"></partial-loader>

	<b-table
		v-if="!pendingBids.isFetching"

		striped
		hover
		show-empty
		:no-local-sorting="true"
		:no-sort-reset="true"
		:sort-by.sync="sort"
		:sort-desc.sync="sortDesc"
		@sort-changed="onSortChanged"
		:items="pendingBids.data"
		:fields="fields"
		:per-page="parseInt(pendingBids.meta.perPage)"
		:empty-text="$t('pendingBid.listEmptyText')"
		:empty-filtered-text="$t('pendingBid.listEmptyFilteredText')"
	>

		<template v-slot:cell(created_at)="row">
  			{{ row.item.created_at | smallDate }}
  		</template>

		<template v-slot:cell(auction_order_number)="row">
  				  <span v-if="row.item.item.auction_order_number">{{ row.item.item.auction_order_number }}</span>
			      <span v-else><i>-</i></span>
  		</template>

		<template v-slot:cell(price)="row">
	             <span class="price">{{ Math.round(row.item.price) }}</span>
	    </template>

		<template v-slot:cell(buyer)="row">
            <span v-if="sort != 'buyer'">{{ row.item.buyer.full_name_simple }}</span>
            <span v-if="sort == 'buyer'">{{ row.item.buyer.last_name }}, {{ row.item.buyer.first_name }} {{ row.item.buyer.last_name_prefix }}</span>
        </template>

		<template v-slot:cell(item_id)="row">
            <router-link :to="{ name: 'item', params: { itemId: row.item.item.id } }">
				<strong>{{ row.item.item.seller.code }}-{{ row.item.item.number }}</strong> {{ row.item.item.title }}
			</router-link>
        </template>

		<template v-slot:cell(actions)="row">
			<b-btn-group class="pull-right">

				<b-btn variant="outline-danger"
					   @click="showDeleteModal(row.item)">
					   <i class="fa fa-fw fa-trash"></i>
				</b-btn>

				<b-btn variant="outline-primary" :to="{ name: 'pendingBidEdit', params: { itemId: row.item.item.id, pendingBidId: row.item.id } }">
                	<i class="fa fa-fw fa-edit"></i>
            	</b-btn>

			</b-btn-group>
        </template>

	</b-table>

	<delete-dialog-modal ref="deleteModal"
	                     :delete-name="selectedDeleteItemName"
	                     :item="selectedDeleteItem"
	                     :isDeleting="isDeleting"
	                     @confirm="deleteBid"></delete-dialog-modal>

	<b-pagination-nav :link-gen="linkGen"
	                  :number-of-pages="pendingBids.meta.totalPages"
	                  :value="pendingBids.meta.currentPage"
	                  use-router
	                  class="pull-right" />

</div>
</template>

<style lang="scss">
table th.sorting{
	white-space: nowrap;
}
table.b-table>thead>tr>th.sorting::before{
	font-family: FontAwesome;
	content: "\f0d7";
	right:0.25em;
	bottom:-6px;
}
table.b-table>thead>tr>th.sorting::after{
	font-family: FontAwesome;
	content: "\f0d8";
	bottom:2px;
	position: absolute;
}
.num{
	text-align: right;
}
.no-wrap{
	white-space: nowrap;
}
.price {
	white-space: nowrap;

}
</style>

<script>
import { isNil } from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import { smallDate } from '@/filters/date-filters';
import PartialLoader from '@/components/elements/PartialLoader';
import DeleteDialogModal from '@/components/elements/Edit/DeleteDialogModal';
import RequestHelper from '@/helpers/request-helper';
import { stringifyQuery, filterParams } from '@/helpers/url-helper';

const requestHelper = new RequestHelper();

export default {
	components: {
		PartialLoader,
		DeleteDialogModal
	},
	filters: {
		smallDate
	},
	data() {
		return {
			fields: [
				{ key: 'created_at', label: this.$t('common.date'), sortable: true, class: 'no-wrap' },
				{ key: 'auction_order_number', label: this.$t('item.auctionOrderNumberList'), sortable: true, class:'num' },
				{ key: 'pending_bid_number', label: this.$t('pendingBid.numberSmall'), sortable: true, class:'num' },
				{ key: 'price', label: `${this.$t('common.bidPrice')} (€)`, sortable: true, class:'num' },
				{ key: 'buyer', label: this.$t('common.buyer'), sortable: true },
				{ key: 'item_id', label: this.$t('common.item'),  sortable: true },
				{ key: 'actions', label: this.$t('common.actions') }
			],
			currentPage: 1,
			perPage: 50,
			selectedDeleteItem: null,
			sort: null,
			sortDesc: true,
			filter: '',
			defaults: {
				sort: 'created_at',
				sortDesc: true,
			}
		}
	},
	computed: {
		...mapGetters('pendingBidList', [
			'pendingBids',
			'isDeleting'
		]),
		selectedDeleteItemName() {
			if (!this.selectedDeleteItem) return '';

			return this.$t('pendingBid.deleteMessage', { pendingBid: this.selectedDeleteItem.item.title });
		}
	},
	watch: {
		'$route': 'fetchData',
		'filter': 'onFilterChanged'
	},
	methods: {
		...mapActions({
			fetchAuctionPendingBids: 'pendingBidList/fetchAuctionPendingBids',
			deleteBidAction: 'pendingBidList/deleteBid',
			flashMessage: 'flashMessages/flashMessage'
		}),

		/**
		 * Link Gen
		 *
		 * Generate link for pagination
		 *
		 * @param {number} pageNumber
		 * @return {string}
		 */
		linkGen(pageNumber) {
			return stringifyQuery(this.getQuery(pageNumber));
		},

		/**
		 * Get query
		 *
		 * Get the object with query data
		 *
		 * @param {number} page optional parameter page
		 * @return {object} 	object with query data
		 */
		getQuery(page) {

			page = page || 1;

			return filterParams({
				page: page,
				sort: this.sort,
				sortDesc: this.sortDesc,
				filter: this.filter
			});

		},

		/**
		 * Show delete modal for the bid
		 * @param  {Object} bid
		 */
		showDeleteModal(bid) {
			this.selectedDeleteItem = bid;
			this.$refs.deleteModal.show();
		},

		/**
		 * Delete the bid and refresh the bids
		 * @param  {Object} bid  Bid that will be deleted
		 * @return {Promise}
		 */
		deleteBid(bid) {

			return this.deleteBidAction({ itemId: bid.item.id, bidId: bid.id })
				.then(() => {

					this.$refs.deleteModal.hide()
					this.fetchData();
					this.selectedDeleteItem = null;

				});
		},

		/**
		 * Reset Sorting
		 *
		 * Set sorting back to its default values
		 */
		resetSorting(updateData = false) {
			this.sort = this.defaults.sort;
			this.sortDesc = this.defaults.sortDesc;
			this.filter = '';

			if(updateData) this.onSortChanged({ sortBy: this.sort, sortDesc: this.sortDesc });
		},

		/**
		 * On Sorting Changed
		 *
		 * Set order and sort on change
		 *
		 * @param {object} ctx
		 */
		onSortChanged(ctx) {

			this.sort = ctx.sortBy;
			this.sortDesc = ctx.sortDesc;

			this.filterData();
		},

		/**
		 * On Filter Changed
		 *
		 * Function that will be called when filter value has changed
		 */
		onFilterChanged() {
			if (this.filter.length > 0) {
				this.sort = 'created_at';
				this.sortDesc = true;
			} else {
				this.sort = 'created_at';
			}

			this.filterData();
		},

		/**
		 * Filter data
		 *
		 * Get data based on query filter
		 */
		filterData() {
			const path = this.$router.resolve({ query: this.getQuery() }).href;

			requestHelper.delayRequest(500)
				.then(() => this.$router.replace({ path: path }))
				.catch(rejected => {});
		},

		/**
		 * Fetch pending bids data
		 */
		fetchData() {

			const params = this.$route.params;
			const query = this.$route.query;

			const currentPage = Number.parseInt(query.page);
			this.currentPage = isNaN(currentPage) ? 1 : currentPage;

			return this.fetchAuctionPendingBids({
				auctionId: params.auctionId,
				params: {
					total: this.perPage,
					page: this.currentPage,
					order: this.sortDesc ? 'desc' : 'asc',
					sort: this.sort,
					query: query.filter ? query.filter : '',
				}
			});
		},

		/**
		 * Parse route query and set values
		 */
		parseQuery() {

			const query = this.$route.query;

			if(!query) return;
			if(query.filter) this.filter = query.filter;
			if(query.sort) this.sort = query.sort;
			if(query.sortDesc) this.sortDesc = query.sortDesc;

		}
	},
	beforeMount() {
		// set defaults
		this.resetSorting();
	},
	created() {
		this.resetSorting();
		this.parseQuery();
		this.fetchData();
	}
}
</script>



// WEBPACK FOOTER //
// PendingBidList.vue?81af4e38